import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import { Loader2, Plus, RefreshCw, ScanLine, TicketPercent } from "lucide-react";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import Modal from "@/components/Modal";
import BusinessCouponCard, {
  type BusinessCouponStats,
} from "@/components/business/BusinessCouponCard";
import StudentQRScanner from "@/components/student/StudentQRScanner";
import { useBusinessCoupons } from "@/hooks/useBusinessCoupons";
import { GetBusinessDashboardStatsAPI } from "@/services/api/business/getBusinessDashboardStats";
import { DeleteBusinessCouponAPI } from "@/services/api/coupon/deleteBusinessCoupon";
import type { BusinessCoupon } from "@/utils/businessCoupon";

/**
 * Business coupon centre — the web port of the RN
 * `BusinessCouponManageScreen` coupon tab: the coupons created by the business
 * as a card grid, the per-coupon QR redemption code, and the shared QR scanner
 * for redeeming a customer's coupon.
 */
const BusinessCouponsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    coupons,
    loading,
    refreshing,
    loadingMore,
    hasMore,
    refresh,
    loadMore,
  } = useBusinessCoupons();

  const [qrCoupon, setQrCoupon] = useState<BusinessCoupon | null>(null);
  const [scannerVisible, setScannerVisible] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState<BusinessCoupon | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [statsMap, setStatsMap] = useState<
    Map<string, BusinessCouponStats>
  >(new Map());

  /** Lifetime per-coupon reach & engagement, shown on each card. */
  const loadStats = useCallback(async () => {
    const resp = await GetBusinessDashboardStatsAPI({});
    if (resp?.status !== 0) {
      setStatsMap(new Map());
      return;
    }
    const map = new Map<string, BusinessCouponStats>();
    for (const item of resp.result?.coupons ?? []) {
      if (!item.coupon_id) continue;
      map.set(String(item.coupon_id), {
        reads: item.reads,
        readers: item.readers,
        redeemed: item.redeemed,
        used: item.used,
      });
    }
    setStatsMap(map);
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleRefresh = () => {
    refresh();
    loadStats();
  };

  const handleDeleteCoupon = async () => {
    if (!couponToDelete) return;
    setDeleting(true);
    const resp = await DeleteBusinessCouponAPI({ coupon_id: couponToDelete.id });
    setDeleting(false);
    setCouponToDelete(null);
    if (resp?.status === 0) {
      await refresh();
      await loadStats();
      window.alert(t("Coupon Deleted"));
      return;
    }
    window.alert(
      String(resp?.result?.message ?? resp?.error ?? t("please_try_again")),
    );
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{t("Coupons")}</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-60"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            {t("Refresh")}
          </button>
          <button
            type="button"
            onClick={() => setScannerVisible(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
          >
            <ScanLine size={14} />
            {t("Business: scan customer coupon")}
          </button>
          <Link
            to="/portal/coupons/new"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#ffa217] px-4 py-2 text-xs font-bold text-white transition hover:bg-[#f59200]"
          >
            <Plus size={14} />
            {t("Create Coupon")}
          </Link>
        </div>
      </div>

      {loading ? (
        <AdminListSkeleton count={4} />
      ) : coupons.length === 0 ? (
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="MaterialCommunityIcons"
            iconName="ticket-percent-outline"
            title={t("No coupons")}
            message={t("Create New Coupon")}
            actionLabel={t("Create Coupon")}
            onAction={() => navigate("/portal/coupons/new")}
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {coupons.map((coupon) => (
              <BusinessCouponCard
                key={coupon.id}
                coupon={coupon}
                stats={statsMap.get(coupon.id)}
                onShowQr={setQrCoupon}
                onScan={() => setScannerVisible(true)}
                onDelete={setCouponToDelete}
              />
            ))}
          </div>

          {hasMore && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-60"
              >
                {loadingMore && <Loader2 size={14} className="animate-spin" />}
                {t("Load more")}
              </button>
            </div>
          )}
        </>
      )}

      {/* Coupon redemption code: the customer scans this to redeem the coupon. */}
      <Modal visible={Boolean(qrCoupon)} onClose={() => setQrCoupon(null)}>
        {qrCoupon && (
          <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
            <div className="mb-3 flex items-center justify-center gap-2 text-sm font-semibold text-gray-900">
              <TicketPercent size={16} className="text-[#ffa217]" />
              {String(qrCoupon.coupon_name ?? t("Coupon"))}
            </div>
            <div className="inline-block rounded-xl border border-gray-100 bg-white p-3">
              <QRCode value={qrCoupon.id} size={200} />
            </div>
            <p className="mt-3 text-xs text-gray-500">
              {t("Customer scans to redeem")}
            </p>
            <button
              type="button"
              onClick={() => setQrCoupon(null)}
              className="mt-4 w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
            >
              {t("Close")}
            </button>
          </div>
        )}
      </Modal>

      <Modal
        visible={scannerVisible}
        onClose={() => setScannerVisible(false)}
      >
        <StudentQRScanner
          active={scannerVisible}
          onClose={() => setScannerVisible(false)}
        />
      </Modal>

      <Modal
        visible={Boolean(couponToDelete)}
        onClose={() => setCouponToDelete(null)}
      >
        <div className="rounded-2xl bg-white p-5 shadow-lg">
          <h2 className="text-base font-bold text-gray-900">
            {t("Delete Coupon")}
          </h2>
          <p className="mt-2 text-xs leading-5 text-gray-500">
            {t("Delete Coupon Confirm")}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => setCouponToDelete(null)}
              disabled={deleting}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-bold text-gray-600 transition hover:border-gray-300 disabled:opacity-60"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handleDeleteCoupon}
              disabled={deleting}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-red-500 py-2.5 text-sm font-bold text-white transition hover:bg-red-600 disabled:opacity-60"
            >
              {deleting && <Loader2 size={14} className="animate-spin" />}
              {t("Delete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessCouponsPage;
